import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DayPickerRangeController } from 'react-dates'
import { toUpper } from 'lodash'
import moment from 'moment'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { setStartDate } from '../../../redux/actions/setFilters'
import { setEndDate } from '../../../redux/actions/setFilters'
import { enableDateFilterLabel } from '../../../configuration/configs'
import useKeyPressEvent from '../../../utilities/events/useKeyPressEvent'
import FilterPopoverWrapper from '../../elements/FilterPopoverWrapper'
import labels from '../../../configuration/labels'

import CaretDownIcon from '../../../assets/icons/caret-down.svg'
import NextArrow from '../../../assets/icons/datepicker-arrow-right.svg'
import PrevArrow from '../../../assets/icons/datepicker-arrow-left.svg'

import '../../../styles/scss/components/filters/datepicker-range.scss'

const DatePickerRange = ({ rangeLabel, setRangeLabel }) => {
  const currentStartDate = useSelector(state => state.filtersData.startDate)
  const currentEndDate = useSelector(state => state.filtersData.endDate)

  const dispatch = useDispatch()
  const isSmallScreen = window.innerWidth < 769

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [focusedInput, setFocusedInput] = useState(null)
  const [activeBtn, setActiveBtn] = useState(labels.datepickerFilterBtnToday)
  const [tempStartDate, setTempStartDate] = useState(moment(currentStartDate))
  const [tempEndDate, setTempEndDate] = useState()
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    if (currentStartDate) {
      setTempStartDate(moment(currentStartDate))
    }
    if (currentEndDate) {
      setTempEndDate(moment(currentEndDate))
    } else {
      setActiveBtn(labels.datepickerFilterBtnToday)
      setTempEndDate()
    }
    setIsActive(!(rangeLabel === 'Date'))
  }, [currentStartDate, currentEndDate, isPopoverOpen, rangeLabel])

  const DateFilterHeaderBtn = ({ label, activeBtn, setActiveBtn, onClick }) => (
    <button
      aria-label={label}
      className={`btn btn-submit-datepicker ${activeBtn === label ? 'active' : ''}`}
      onClick={() => {
        setActiveBtn(label)
        onClick()
      }}
    >
      {toUpper(label)}
    </button>
  )

  const togglePopover = () => {
    setIsPopoverOpen(status => !status)
  }
  const isOutsideRange = day => {
    return (
      day.isBefore(moment(), 'day') ||
      day.isAfter(moment().startOf('month').add(14, 'months').endOf('month'))
    )
  }

  function onOpenPopover(input) {
    setFocusedInput(input)
    setIsPopoverOpen(!isPopoverOpen)
  }
  function onCancelPopover() {
    setFocusedInput(null)
    setIsPopoverOpen(false)
  }
  useKeyPressEvent('Escape', onCancelPopover)

  function onDatesChange({ startDate, endDate }) {
    setTempStartDate(startDate)
    setTempEndDate(endDate)
  }

  const resetDates = useCallback(() => {
    //set start/end dates back to default
    dispatch(setStartDate(moment().format('YYYY-MM-DD')))
    dispatch(setEndDate(''))
    setTempStartDate(moment())
    setTempEndDate(null)
    setRangeLabel(labels.filteredEventsDate)
    setActiveBtn(labels.datepickerFilterBtnToday)
  }, [dispatch, setRangeLabel])

  const onSubmitFilter = useCallback(() => {
    setFocusedInput(null)
    dispatch(setStartDate(tempStartDate.format('YYYY-MM-DD')))
    dispatch(
      setEndDate(
        tempEndDate ? tempEndDate.format('YYYY-MM-DD') : tempStartDate.format('YYYY-MM-DD')
      )
    )
    setActiveBtn('')
    tempStartDate.isSame(tempEndDate) || !tempEndDate
      ? setRangeLabel(tempStartDate?.format('MMM D'))
      : setRangeLabel(tempStartDate?.format('MMM D') + ' - ' + tempEndDate?.format('MMM D'))
    togglePopover()
  }, [tempStartDate, tempEndDate, dispatch, setRangeLabel])

  const onClear = () => {
    setFocusedInput(null)
    setIsPopoverOpen(false)
    resetDates()
  }

  return (
    <div
      id="date-filter"
      className="decoupled-events__filters-bar-datepicker"
    >
      {enableDateFilterLabel && labels.filterLabelDates && <h4>{labels.filterLabelDates}</h4>}
      <FilterPopoverWrapper
        alignment="center"
        containerParent="date-filter"
        popoverClassName="popover-datepicker"
        isPopoverOpen={isPopoverOpen}
        onClosePopover={onCancelPopover}
        onClear={onClear}
        onSubmit={onSubmitFilter}
        PopoverTab={
          <button
            className={`filter-tab ${isActive ? 'active' : ''}`}
            onClick={() => onOpenPopover('startDate')}
          >
            {toUpper(rangeLabel)}
            <img src={CaretDownIcon} alt={isPopoverOpen ? 'Close' : 'Open'} />
          </button>
        }
        hasClearBtn={true}
      >
        <div className="datepicker__header">
          <DateFilterHeaderBtn
            label={labels.datepickerFilterBtnToday}
            activeBtn={activeBtn}
            setActiveBtn={setActiveBtn}
            onClick={() => {
              setFocusedInput(null)
              dispatch(setStartDate(moment().format('YYYY-MM-DD')))
              dispatch(setEndDate(moment().format('YYYY-MM-DD')))
              setRangeLabel(moment().format('MMM D'))
            }}
            setIsPopoverOpen={setIsPopoverOpen}
          />
          <DateFilterHeaderBtn
            label={labels.datepickerFilterBtnTomorrow}
            activeBtn={activeBtn}
            setActiveBtn={setActiveBtn}
            onClick={() => {
              setFocusedInput(null)
              dispatch(setStartDate(moment().add(1, 'day').format('YYYY-MM-DD')))
              dispatch(setEndDate(moment().add(1, 'day').format('YYYY-MM-DD')))
              setRangeLabel(moment().add(1, 'day').format('MMM D'))
            }}
            setIsPopoverOpen={setIsPopoverOpen}
          />
          <DateFilterHeaderBtn
            label={labels.datepickerFilterBtnNextWeek}
            activeBtn={activeBtn}
            setActiveBtn={setActiveBtn}
            onClick={() => {
              setFocusedInput(null)
              let start = moment().add(1, 'week').startOf('week')
              let end = moment().add(1, 'week').endOf('week')
              dispatch(setStartDate(start.format('YYYY-MM-DD')))
              dispatch(setEndDate(end.format('YYYY-MM-DD')))
              setRangeLabel(start.format('MMM D') + ' - ' + end.format('MMM D'))
            }}
            setIsPopoverOpen={setIsPopoverOpen}
          />
          <DateFilterHeaderBtn
            label={labels.datepickerFilterBtnNextMonth}
            activeBtn={activeBtn}
            setActiveBtn={setActiveBtn}
            onClick={() => {
              setFocusedInput(null)
              let start = moment().add(1, 'month').startOf('month')
              let end = moment().add(1, 'month').endOf('month')
              dispatch(setStartDate(start.format('YYYY-MM-DD')))
              dispatch(setEndDate(end.format('YYYY-MM-DD')))
              setRangeLabel(start.format('MMM D') + ' - ' + end.format('MMM D'))
            }}
            setIsPopoverOpen={setIsPopoverOpen}
          />
        </div>
        <div className="datepicker-range">
          <DayPickerRangeController
            startDate={tempStartDate}
            endDate={tempEndDate}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            isOutsideRange={isOutsideRange}
            onDatesChange={onDatesChange}
            initialVisibleMonth={() => (tempStartDate ? tempStartDate : moment())}
            keepOpenOnDateSelect
            hideKeyboardShortcutsPanel
            orientation={isSmallScreen ? 'vertical' : 'horizontal'}
            numberOfMonths={isSmallScreen ? 1 : 2}
            navNext={
              <button className="datepicker-range__arrow next">
                <img src={NextArrow} alt="Date Picker Next" />
              </button>
            }
            navPrev={
              <button className="datepicker-range__arrow prev">
                <img src={PrevArrow} alt="Date Picker Prev" />
              </button>
            }
          />
        </div>
      </FilterPopoverWrapper>
    </div>
  )
}

export default DatePickerRange
